import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import {
    DialogContent, DialogTitle, Stack, Typography, Grid, IconButton, Tooltip,
    Dialog, Divider, Box, CircularProgress, Chip, Table, TableHead, TableRow,
    TableCell, TableBody, Button
} from '@mui/material'
import { EditOutlined, CloseOutlined, PlusCircleOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons'

import { useEventBusContext } from 'services/provider/EventBusProvider'
import { getPermissionKeys } from 'utils/util'
import {
    EVENT_BANNER_DETAIL_INIT, EVENT_BANNER_UPDATE_INIT, EVENT_BANNER_UPDATE_SUCCESS,
    EVENT_BANNER_SLIDE_ADD_INIT, EVENT_BANNER_SLIDE_UPDATE_INIT,
    EVENT_BANNER_SLIDE_DELETE_INIT, EVENT_BANNER_SLIDE_UPDATE_SUCCESS, EVENT_BANNER_SLIDE_DETAIL_INIT
} from 'models/events/banner.event'
import { bannerDetail } from 'models/banner'
import { getImageUrl } from 'utils/helpers'
import { useApiData } from 'services/hook/useApiData'
import { labelFor, BANNER_VIEW_TYPES, BANNER_PAGE_TYPES } from 'constants/banner'
import { BannerSlideAddDialog } from './BannerSlide.Create'
import { BannerSlideEditDialog } from './BannerSlide.Update'
import { BannerSlideDetailDialog } from './BannerSlide.Detail'
import { BannerSlideDeleteDialog } from './BannerSlide.Delete'

export const BannerDetailDialog = () => {
    const [bannerId, setBannerId] = useState(null)
    const eventbus = useEventBusContext()

    useEffect(() => {
        return eventbus.on(EVENT_BANNER_DETAIL_INIT, (data) => setBannerId(data.id))
    }, [eventbus])

    return (
        <Dialog open={bannerId != null} maxWidth="md" fullWidth scroll={'body'} onClose={() => setBannerId(null)}>
            {bannerId && <BannerDetail bannerId={bannerId} onClose={() => setBannerId(null)} />}
            <BannerSlideAddDialog bannerId={bannerId} />
            <BannerSlideEditDialog />
            <BannerSlideDetailDialog />
            <BannerSlideDeleteDialog />
        </Dialog>
    )
}

const BannerDetail = ({ bannerId, onClose }) => {
    const eventbus = useEventBusContext()
    const permissionsKeys = getPermissionKeys('banner')

    const [data, , loading, loadData] = useApiData({ data: {} }, {}, (params, setData) => {
        bannerDetail(bannerId)
            .then((res) => setData(res.data || res))
            .catch(() => setData({}))
    })

    useEffect(() => {
        return eventbus.on(EVENT_BANNER_UPDATE_SUCCESS, () => loadData())
    }, [eventbus, loadData])

    useEffect(() => {
        return eventbus.on(EVENT_BANNER_SLIDE_UPDATE_SUCCESS, () => loadData())
    }, [eventbus, loadData])

    useEffect(() => {
        if (bannerId) loadData()
    }, [bannerId])

    if (loading) {
        return (
            <>
                <DialogTitle>Loading...</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={50} />
                    </Box>
                </DialogContent>
            </>
        )
    }

    if (!data || !data.name) {
        return (
            <>
                <DialogTitle>Banner Not Found</DialogTitle>
                <DialogContent>
                    <Typography variant="h6" color="error" sx={{ p: 4, textAlign: 'center' }}>Unable to load banner details.</Typography>
                </DialogContent>
            </>
        )
    }

    return (
        <>
            <DialogTitle sx={{ pb: 2, pt: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2} justifyContent="space-between">
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{data.name}</Typography>
                        <Stack direction="row" spacing={1}>
                            <Chip label={data.status === 'active' ? 'Active' : 'Inactive'} size="small" color={data.status === 'active' ? 'success' : 'error'} />
                            <Chip label={labelFor(BANNER_VIEW_TYPES, data.view_type)} size="small" variant="outlined" />
                            <Chip label={labelFor(BANNER_PAGE_TYPES, data.page_type)} size="small" variant="outlined" />
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {permissionsKeys.can_update && (
                            <Tooltip title="Edit Banner">
                                <IconButton color="primary" onClick={() => { onClose(); eventbus.fire(EVENT_BANNER_UPDATE_INIT, bannerId) }}>
                                    <EditOutlined />
                                </IconButton>
                            </Tooltip>
                        )}
                        <IconButton onClick={onClose}><CloseOutlined /></IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                <Stack spacing={3}>
                    {data.description && (
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Description</Typography>
                            <Typography variant="body1" color="text.secondary">{data.description}</Typography>
                        </Box>
                    )}

                    <Grid container justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Slides</Typography>
                        {permissionsKeys.can_update && (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlusCircleOutlined />}
                                onClick={() => eventbus.fire(EVENT_BANNER_SLIDE_ADD_INIT, bannerId)}
                            >
                                Add Slide
                            </Button>
                        )}
                    </Grid>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Image</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Link</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Order</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data.slides || []).map((slide) => (
                                <TableRow key={slide.id}>
                                    <TableCell>
                                        <Box component="img" src={getImageUrl(slide.image_url)} alt={slide.title} sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }} />
                                    </TableCell>
                                    <TableCell>{slide.title}</TableCell>
                                    <TableCell>{slide.link || '-'}</TableCell>
                                    <TableCell>
                                        <Chip label={slide.status === 'active' ? 'Active' : 'Inactive'} size="small" color={slide.status === 'active' ? 'success' : 'error'} />
                                    </TableCell>
                                    <TableCell>{slide.sort_order}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => eventbus.fire(EVENT_BANNER_SLIDE_DETAIL_INIT, slide)}>
                                            <EyeOutlined />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => eventbus.fire(EVENT_BANNER_SLIDE_UPDATE_INIT, slide)}>
                                            <EditOutlined />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => eventbus.fire(EVENT_BANNER_SLIDE_DELETE_INIT, slide)}>
                                            <DeleteOutlined style={{ color: '#ff4d4f' }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
            </DialogContent>
        </>
    )
}

BannerDetail.propTypes = { bannerId: PropTypes.number.isRequired, onClose: PropTypes.func.isRequired }

export default BannerDetail